import { useMutation } from '@apollo/client'
import { Box, FormModal, Link, Text } from '@chaine/keychaine'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_TQL_TRAX_APP, EnableTQLTraxAppInput } from '../data/mutations'

type FieldValues = { clientID: string; ocpApimSubscriptionKey: string; password: string; username: string }

const Footer = ({ formError }: { formError: string }) => (
  <Box>
    <Text color="red.600">{formError}</Text>
    <Text>
      {`See `}
      <Link
        color="blue.600"
        href="https://handbook.chaineapp.com/product-guide/carriers/connect-eld/elds/tql-trax"
        target="_blank"
        rel="noopener noreferrer"
      >
        detailed steps here.
      </Link>
    </Text>
  </Box>
)

/**
 * Saves TQLTrax app's ocpApimSubscriptionKey, password, username and clientID
 *
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const TQLTraxModal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { showToast } = Toast()
  const [formError, setFormError] = useState('')

  const [enableTQLTraxApp, { loading }] = useMutation<
    { enableTQLTraxApp: { clientID: string; ocpApimSubscriptionKey: string; password: string; username: string } },
    EnableTQLTraxAppInput
  >(ENABLE_TQL_TRAX_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: `TQLTrax app is enabled successfully!`
      })
      refetchApps()
      onClose()
      setFormError('')
    },
    onError: (err) => {
      if (err.message === 'Incorrect API key') {
        setFormError('Invalid api key or username/password. Please try again.')
        showToast({
          status: 'error',
          title: `Invalid api key or username/password. Please try again.`
        })
      } else {
        setFormError(`Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`)
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    enableTQLTraxApp({
      variables: {
        appType,
        clientID: event.clientID,
        ocpApimSubscriptionKey: event.ocpApimSubscriptionKey,
        password: event.password,
        username: event.username
      }
    })
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={closeModal}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an admin TQL Trax account. Enter your TQL Trax Client ID, Username, Password and Ocp Apim Subscription Key`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        footer={formError ? <Footer formError={formError} /> : <></>}
        modalItems={[
          {
            labelText: 'TQL Trax Client ID',
            name: 'clientID',

            placeHolder: 'Enter your TQL Trax Client ID',
            type: 'input'
          },
          {
            labelText: 'TQL Trax Username',
            name: 'username',

            placeHolder: 'Enter your TQL Trax Username',
            type: 'input'
          },
          {
            labelText: 'TQL Trax Password',
            name: 'password',

            placeHolder: 'Enter your TQL Trax Password',
            type: 'input'
          },
          {
            labelText: 'TQL Trax Ocp Apim Subscription Key',
            name: 'ocpApimSubscriptionKey',

            placeHolder: 'Enter your TQL Trax Ocp Apim Subscription Key',
            type: 'input'
          }
        ]}
      />
    </>
  )
})

TQLTraxModal.displayName = 'TQLTraxModal'

export { TQLTraxModal as TQLTraxModal }
